import { useState, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  IconButton,
  Typography,
  Container,
  Box,
  Button,
  Alert,
  useTheme as useMuiTheme
} from '@mui/material'
import {
  Map as MapIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material'
import ProjectPageHeader from '../../components/ProjectPageHeader'
import { useProjectStore, type ParkingLot } from '../../map-editor-shared/store'
import { useObjectTypeStore } from '../../map-editor-shared/store/objectTypeStore'
import { useProjects, useUpdateProject, useDeleteProject } from '../../map-editor-shared/hooks'
import ParkingLotCard from '../../map-editor-widgets/dashboard/ParkingLotCard'
import CreateLotModal from '../../map-editor-widgets/dashboard/CreateLotModal'
import DeleteConfirmModal from '../../map-editor-widgets/dashboard/DeleteConfirmModal'
import { TemplateSelectModal, loadTemplate, type TemplateId, numberToTemplateId } from '../../map-editor-features/template'
import { mapDataService } from '../../map-editor-shared/api'
import { useProjectCreation } from './useProjectCreation'
import { DwgConvertButton } from '../../map-editor-features/csv/ui/DwgConvertButton'
import { storage } from '../../map-editor-shared/lib/utils'
import styles from './DashboardPage.module.css'
import { log, LogCategory } from '../../map-editor-shared/lib/logger'
import { useTheme } from '../../map-editor-shared/context/ThemeContext'

export default function DashboardPage() {
  const navigate = useNavigate()
  const { projectId } = useParams<{ projectId: string }>()

  // Store initial site project ID to prevent it from changing during navigation
  const siteProjectIdRef = useRef<string>(projectId || '')
  if (projectId && !siteProjectIdRef.current) {
    siteProjectIdRef.current = projectId
  }

  // ALL HOOKS MUST BE DECLARED BEFORE ANY EARLY RETURNS (Rules of Hooks)
  const muiTheme = useMuiTheme()
  const { theme: currentTheme, toggleTheme } = useTheme()
  const { updateLot, deleteLot, setCurrentLot, setServerProject, getLotById } = useProjectStore()

  // API 호출
  const { data: apiProjects, isLoading: isLoadingProjects } = useProjects(Number(siteProjectIdRef.current) || 0)
  const updateProjectMutation = useUpdateProject()
  const deleteProjectMutation = useDeleteProject()

  // 프로젝트 생성 + 템플릿 적용 공용 경로(가이드 셋업 페이지와 공유).
  const { createProject, applyTemplateToProject } = useProjectCreation(siteProjectIdRef.current)

  // Local state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [editingLot, setEditingLot] = useState<ParkingLot | null>(null)
  const [deletingLotId, setDeletingLotId] = useState<string | null>(null)
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dwgMessage, setDwgMessage] = useState<{ kind: 'info' | 'success' | 'error'; text: string } | null>(null)
  const handleDwgMessage = useCallback((kind: 'info' | 'success' | 'error', text: string) => setDwgMessage({ kind, text }), [])

  // API 프로젝트 데이터를 화면에 표시하기 위한 형식으로 변환
  const displayProjects = apiProjects?.map(project => ({
    id: project.id.toString(),
    name: project.name,
    thumbnail: project.thumbnail || undefined,
    cctvIpPrefix: project.cctv_ip_prefix || undefined,
    description: project.description || undefined,
    inSiteUse: project.in_site_use ?? false,
    created: project.created_at,
    modified: project.updated_at,
  })) || []

  const handleLogout = () => {
    // scoped storage에서 제거 (map-editor- prefix 자동 추가)
    storage.remove('accessToken')
    navigate('/login')
  }

  const handleCreateNew = () => {
    setEditingLot(null)
    setError(null)
    setIsCreateModalOpen(true)
  }

  const handleEdit = (lot: ParkingLot) => {
    setEditingLot(lot)
    setError(null)
    setIsCreateModalOpen(true)
  }

  // 현장 사용 표시 켜기/끄기 — 카드 토글이 이 값만 보낸다(나머지 필드는 서버가 유지).
  const handleToggleSiteUse = async (lot: ParkingLot) => {
    const projectId = parseInt(lot.id, 10)
    if (isNaN(projectId)) return
    try {
      await updateProjectMutation.mutateAsync({ id: projectId, data: { in_site_use: !lot.inSiteUse } })
    } catch (err) {
      setError(err instanceof Error ? err.message : '현장 사용 표시를 바꾸지 못했습니다.')
    }
  }

  const handleDeleteClick = (id: string) => {
    setDeletingLotId(id)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (deletingLotId) {
      try {
        // localStorage에서 삭제
        deleteLot(deletingLotId)

        // API로 삭제 (project_id를 숫자로 변환)
        const projectId = parseInt(deletingLotId, 10)
        if (!isNaN(projectId)) {
          await deleteProjectMutation.mutateAsync(projectId)
        }

        setIsDeleteModalOpen(false)
        setDeletingLotId(null)
      } catch (err) {
        setError('Failed to delete parking lot. Please try again.')
      }
    }
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false)
    setDeletingLotId(null)
  }

  const handleSubmit = async (data: { name: string; description?: string; floorName?: string; cctvIpPrefix?: string }) => {
    try {
      if (editingLot) {
        // `floorName` is intentionally ignored on edit — floor renaming happens in the editor's floor tabs.
        const { floorName: _ignoredFloorName, ...editFields } = data

        // 서버가 원본이다. 목록은 서버에서 오므로 이 브라우저 localStorage 에 없는 맵이 대부분이라,
        // 로컬 갱신(updateLot)은 있을 때만 한다 — 없으면 "Parking lot with id … not found" 를
        // 던져 서버 저장까지 막았다.
        const projectId = parseInt(editingLot.id, 10)
        if (!isNaN(projectId)) {
          await updateProjectMutation.mutateAsync({
            id: projectId,
            data: {
              name: data.name,
              description: data.description ?? '', // 비우면 서버에서도 비운다
              cctv_ip_prefix: data.cctvIpPrefix ?? '',
            },
          })
        }
        if (getLotById(editingLot.id)) updateLot(editingLot.id, editFields)

        setIsCreateModalOpen(false)
        setEditingLot(null)
        setError(null)
      } else {
        const lotId = await createProject(data)
        setCreatedProjectId(lotId)

        setIsCreateModalOpen(false)
        setError(null)
        // Open template selection modal
        setIsTemplateModalOpen(true)
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('An unexpected error occurred')
      }
    }
  }

  const handleTemplateSelect = async (templateId: string) => {
    if (!createdProjectId) return

    try {
      await applyTemplateToProject(createdProjectId, templateId)

      // Navigate to editor
      const lotId = createdProjectId
      setIsTemplateModalOpen(false)
      setCreatedProjectId(null)
      navigate(`/project/${siteProjectIdRef.current}/map-editor/${lotId}`)
    } catch (err) {
      setError('Failed to apply template. Please try again.')
      setIsTemplateModalOpen(false)
    }
  }

  const handleTemplateModalClose = () => {
    // If user closes template modal without selecting, delete the created project
    if (createdProjectId) {
      deleteLot(createdProjectId)
      setCreatedProjectId(null)
    }
    setIsTemplateModalOpen(false)
  }

  const handleSelectLot = async (lotId: string) => {
    try {

      // For server projects, skip localStorage validation
      // Server projects will be loaded directly in EditorPage
      const serverProject = apiProjects?.find(p => p.id.toString() === lotId)

      if (serverProject) {

        // Fetch map data to get template information
        try {
          const response = await mapDataService.getMapData(Number(lotId))
          const mapDataList = response.data

          // Extract template ID from the first floor's data
          let templateId = 1; // Default to parking template
          let templateName: TemplateId = 'parking';

          if (mapDataList && mapDataList.length > 0 && mapDataList[0].template_id) {
            templateId = mapDataList[0].template_id;
            templateName = numberToTemplateId(templateId);
          }

          // Load the template to get object types
          const template = await loadTemplate(templateName);

          // Create a ParkingLot object for the server project
          // Normalize datetime to ISO 8601 format for export validation
          const parkingLot: ParkingLot = {
            id: serverProject.id.toString(),
            name: serverProject.name,
            thumbnail: serverProject.thumbnail || undefined,
            created: new Date(serverProject.created_at).toISOString(),
            modified: new Date(serverProject.updated_at).toISOString(),
            templateId: templateId,
            templateName: templateName,
            availableObjectTypes: template.objectTypes,
            relationTypes: template.relationTypes,
            assets: template.assets,
            initialObjects: template.initialObjects,
          };

          // Add server project to ProjectStore
          setServerProject(parkingLot);
        } catch (err) {
          // Still add project without template info
          // Normalize datetime to ISO 8601 format for export validation
          const parkingLot: ParkingLot = {
            id: serverProject.id.toString(),
            name: serverProject.name,
            thumbnail: serverProject.thumbnail || undefined,
            created: new Date(serverProject.created_at).toISOString(),
            modified: new Date(serverProject.updated_at).toISOString(),
          };
          setServerProject(parkingLot);
        }
      } else {
        // Set current lot in project store for localStorage projects
        setCurrentLot(lotId)
      }

      // Synchronously load object types from localStorage before navigation
      // This ensures EditorPage mounts with populated object type data
      const objectTypeStoreState = useObjectTypeStore.getState()
      objectTypeStoreState.setCurrentLot(lotId)

      // Navigate to editor with object types already loaded using SITE project ID
      const targetPath = `/project/${siteProjectIdRef.current}/map-editor/${lotId}`
      navigate(targetPath)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err)
      console.error('handleSelectLot error:', err)
      setError(`Failed to open parking lot: ${errorMessage}`)
    }
  }

  const handleViewLot = (lotId: string) => {
    navigate(`/project/${siteProjectIdRef.current}/map-viewer/${lotId}`)
  }

  const handleCloseModal = () => {
    setIsCreateModalOpen(false)
    setEditingLot(null)
    setError(null)
  }

  const deletingLot = deletingLotId ? displayProjects.find((l) => l.id === deletingLotId) : null

  // Sort projects by creation date (newest first)
  // 현장에서 쓰는 맵을 앞에, 그 안에서는 최신순.
  const sortedProjects = [...displayProjects].sort((a, b) => {
    if (a.inSiteUse !== b.inSiteUse) return a.inSiteUse ? -1 : 1
    const dateA = new Date(a.created).getTime()
    const dateB = new Date(b.created).getTime()
    return dateB - dateA // Descending order (newest first)
  })

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: currentTheme === 'dark' ? '#0a0a0a' : '#fafafa'
    }}>
      {/* 공통 ProjectPageHeader (현장 목록 > [현장명] > 맵 에디터 breadcrumb).
          맵 에디터의 자체 ThemeContext 와 충돌하지 않도록 글로벌 테마 토글은 숨기고,
          페이지별 액션(프로젝트 수, 추가 버튼, 맵 에디터 자체 테마 토글) 은 rightExtras 에. */}
      <ProjectPageHeader
        currentPageLabel="맵 에디터"
        currentPageIcon={<MapIcon fontSize="inherit" />}
        hideThemeToggle
        rightExtras={
          <>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mr: 1,
                display: { xs: 'none', sm: 'block' },
                fontSize: '0.875rem',
              }}
            >
              프로젝트 {displayProjects.length}개
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(`/project/${siteProjectIdRef.current}/map-editor/guided`)}
              sx={{ borderRadius: 2 }}
            >
              🧭 가이드 맵 프로젝트 생성
            </Button>
            <DwgConvertButton siteProjectId={Number(siteProjectIdRef.current) || null} onMessage={handleDwgMessage} />
            <Button
              variant="outlined"
              size="small"
              onClick={handleCreateNew}
              startIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              }
              sx={{ borderRadius: 2 }}
            >
              프로젝트 추가
            </Button>
            <IconButton
              onClick={toggleTheme}
              size="small"
              title="맵 에디터 테마"
              sx={{ color: 'text.secondary' }}
            >
              {currentTheme === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </>
        }
      />

      <Container maxWidth="xl" sx={{
        py: { xs: 3, sm: 4 },
        px: { xs: 2, sm: 3, md: 4 }
      }}>

        {dwgMessage && (
          <Alert severity={dwgMessage.kind} onClose={() => setDwgMessage(null)} sx={{ mb: 2 }} data-testid="dwg-convert-message">
            {dwgMessage.text}
          </Alert>
        )}

        {/* Error Message */}
        {error && !isCreateModalOpen && (
          <div className={styles.errorBanner}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
            <button onClick={() => setError(null)} className={styles.errorClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoadingProjects && displayProjects.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
              </svg>
            </div>
            <h2 className={styles.emptyTitle}>Loading projects...</h2>
          </div>
        )}

        {/* Empty State or Project Grid */}
        {!isLoadingProjects && displayProjects.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 7l6-3 6 3 6-3v13l-6 3-6-3-6 3V7z" />
              </svg>
            </div>
            <h2 className={styles.emptyTitle}>No projects yet</h2>
            <p className={styles.emptyDescription}>
              Create your first parking lot project to get started with the map editor.
            </p>
            <button className={styles.emptyButton} onClick={handleCreateNew}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create Your First Project
            </button>
          </div>
        ) : (
          <div className={styles.grid}>
            {sortedProjects.map((project) => (
              <ParkingLotCard
                key={project.id}
                lot={project}
                onEdit={handleEdit}
                onToggleSiteUse={handleToggleSiteUse}
                onDelete={handleDeleteClick}
                onSelect={handleSelectLot}
                onView={handleViewLot}
              />
            ))}
          </div>
        )}
      </Container>

      {/* Modals */}
      <CreateLotModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        editingLot={editingLot}
        onSubmit={handleSubmit}
        error={error}
      />

      <TemplateSelectModal
        isOpen={isTemplateModalOpen}
        onClose={handleTemplateModalClose}
        onSelect={handleTemplateSelect}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        lotName={deletingLot?.name || ''}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

    </Box>
  )
}
